import { styled } from "@mui/material";
import { motion } from "framer-motion";
import {
  Link,
  Outlet,
  UIMatch,
  useLocation,
  useMatches,
  useNavigate,
} from "react-router-dom";
import { useAppSelector } from "../../redux/hooks";
import { escapeUriPath } from "../../utils/StringUtils";
import {
  MOBILE_SCREEN_SIZE,
  TABLET_SCREEN_SIZE,
} from "../AppNavigation/constants";

import { WebAndBrandAppConfig } from "@/redux/types";
import useSiteEntityManager from "@/utils/hooks/useSiteEntityManager";
import {
  BackButton,
  CloseButton,
  getBrandLogo,
  signOut,
} from "@brandclub/common-ui";
import { useContext, useRef, useState } from "react";
import { StoreBrandingType } from "../../types/misc";
import { AppSuspense } from "../AppSuspense";
import { useAsyncCheckoutEnabled } from "../Checkout/components/CheckoutV2/hooks/useAsyncCheckoutEnabled";
import { UserLoginContext } from "../pages/Auth/UserLoginProvider";
import StoreFullScreenLoading from "./StoreFullScreenLoading";
import {
  HeaderBagButton,
  HeaderMenuResponsiveMenu,
} from "./StoreSinglePageHeaderComponents";
import {
  StoreSinglePageWrapperContext,
  useScrollSinglePageWrapperToTop,
} from "./StoreSinglePageWrapperContext";

export const desktopHeaderHeight = 85;
export const mobileHeaderHeight = 60;

export enum StoreNavAction {
  Back = "back",
  SignOut = "signout",
  ToBag = "toBag",
  ToShopDomain = "toShopDomain",
  SkipUserSignUpFlow = "skipUserSignUpFlow",
}

interface StoreNavConfig {
  close?: StoreNavAction;
  back?: StoreNavAction;
  hasViewBagButton?: boolean;
  hasHamburgerMenu?: boolean;
}

const PageHeaderStyled = styled(motion.div)({
  position: "sticky",
  top: 0,
  backgroundColor: "#ffffff",
  height: desktopHeaderHeight,
  width: "100%",
  boxShadow: "0 5px 10px 0 rgba(181, 176, 176, 0.16)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  [`@media (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
  [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    height: mobileHeaderHeight,
  },
  ".control_container": {
    width: "100px",
    height: desktopHeaderHeight,
    [`@media (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
    [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      width: "50px",
      height: mobileHeaderHeight,
    },
  },
  ".back_button": {
    position: "absolute",
    left: "2%",
    cursor: "pointer",
    width: 30,
    height: 30,
    padding: 0,
    outline: "none",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "none",
    ".button_icon": {
      maxHeight: 30,
      maxWidth: 30,
      objectFit: "contain",
      [`@media (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
      [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        maxHeight: 24,
        maxWidth: 24,
      },
    },
  },
  ".close_button": {
    position: "absolute",
    right: "2%",
    cursor: "pointer",
    width: 30,
    height: 30,
    padding: 0,
    outline: "none",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "none",
    ".button_icon": {
      maxHeight: 20,
      maxWidth: 20,
      objectFit: "contain",
      [`@media (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
      [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        maxHeight: 16,
        maxWidth: 16,
      },
    },
  },
  ".single_logo": {
    cursor: "pointer",
    objectFit: "contain",
    height: "101%",
    maxHeight: 70,
    maxWidth: 70,
    borderRadius: "50%",
  },
  ".single_logo--alternative": {
    cursor: "pointer",
    objectFit: "contain",
    height: "101%",
    maxWidth: 150,
  },
});

const PageBodyStyled = styled(motion.div)({
  display: "flex",
  flexDirection: "column",
  "> div": {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
});

/**
 * Checks if width and height are nearly equal within a given threshold.
 *
 * @param width - The element's width.
 * @param height - The element's height.
 * @param threshold - Allowable variance (default is 10).
 * @returns True if |width - height| is less than the threshold; otherwise, false.
 *
 * @example
 * isDimensionsCircular(100, 105); // true
 * isDimensionsCircular(100, 115, 20); // true
 * isDimensionsCircular(100, 120); // false
 * isDimensionsCircular(100, 101, 0); // false
 */
const isDimensionsCircular = (width: number, height: number, threshold = 10) =>
  Math.abs(width - height) < threshold;

export const PageBody = () => {
  const location = useLocation();
  useScrollSinglePageWrapperToTop(location.pathname);
  return (
    <PageBodyStyled
      initial={{ opacity: 0 }}
      className="page_body_outlet"
      sx={{
        justifyContent: "space-between",
        minHeight: `calc(100% - ${desktopHeaderHeight}px)`,
        height: `calc(100% - ${desktopHeaderHeight}px)`, // handle mobile here
        [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
          minHeight: `calc(100% - ${mobileHeaderHeight}px)`,
          height: `calc(100% - ${mobileHeaderHeight}px)`, // handle mobile here
        },
      }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ delay: 0.8, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <AppSuspense fallback={<StoreFullScreenLoading storeHeader={false} />}>
        <Outlet />
      </AppSuspense>
    </PageBodyStyled>
  );
};

const getPageNavConfig = (
  matchs: UIMatch<any, { pageNavConfig?: StoreNavConfig }>[]
): StoreNavConfig => {
  let deepestPageNavConfig: StoreNavConfig = {};

  matchs.forEach((item) => {
    if (item?.handle && item?.handle?.pageNavConfig) {
      deepestPageNavConfig = item.handle.pageNavConfig;
    }
  });

  return deepestPageNavConfig;
};

interface BrandLogoProps {
  brandId?: string;
  loading?: boolean;
  to: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}
const BrandLogo = ({ loading = false, brandId, ...props }: BrandLogoProps) => {
  const [isCircular, setIsCircular] = useState(true); // assumes the logo is circular by default
  if (loading) {
    return null;
  }

  const logoSrc = brandId
    ? getBrandLogo(brandId)
    : "https://media.brandclub.com/brandclub/image_asset/brandclub_logo_no_star.svg";

  return (
    <Link
      style={{
        display: "flex",
        justifyContent: "center",
        height: "100%",
        padding: "7.5px",
        boxSizing: "border-box",
      }}
      {...props}
    >
      <img
        className={isCircular ? "single_logo" : "single_logo--alternative"}
        alt={"single_logo"}
        src={logoSrc}
        onLoad={(e) => {
          const { naturalWidth, naturalHeight } = e.currentTarget;
          if (isDimensionsCircular(naturalWidth, naturalHeight)) {
            setIsCircular(true);
          } else {
            setIsCircular(false);
          }
        }}
      />
    </Link>
  );
};

export function getHeaderLink(
  appConfig: WebAndBrandAppConfig | null | undefined,
  brandName: string | undefined,
  brandId: string | undefined
): { external: boolean; link: string } {
  // Check if it's a custom DTC store with a custom domain
  const customDomain = appConfig?.domainConfig?.customShopDomainName;
  const customDomainHomePage =
    appConfig?.domainConfig?.customShopPageUrls?.homePage;
  const storeBrandingType = appConfig?.domainConfig?.storeBrandingType;
  const isCustomDTCStore =
    storeBrandingType === StoreBrandingType.CustomDTCStore;
  const isCustomStore = storeBrandingType === StoreBrandingType.CustomStore;

  if (isCustomDTCStore && (customDomainHomePage || customDomain)) {
    // ensure we redirect to the correct host depending on what domain the user came from
    const alternateCustomShopDomainNames =
      appConfig?.domainConfig?.alternateCustomShopDomainNames;
    const redirectHost = document.referrer && new URL(document.referrer).host;
    if (
      redirectHost &&
      alternateCustomShopDomainNames?.includes(redirectHost)
    ) {
      return { external: true, link: `https://${redirectHost}/` };
    }
    return {
      external: true,
      link: `https://${customDomainHomePage || customDomain}`,
    };
  }

  // Check if we have both brand name and ID but it's not a custom store
  if (!isCustomStore && brandName && brandId) {
    return {
      external: false,
      link: `/${escapeUriPath(brandName)}/b/${brandId}/home`,
    };
  }

  // Default case
  return { external: false, link: "/" };
}

export const PageHeader = () => {
  const { send } = useContext(UserLoginContext);
  const location = useLocation();
  const brandIdFromLocationState = location?.state?.brandId;
  const brandNameFromLocationState = location?.state?.brandName;
  const appConfig = useAppSelector(({ appConfig }) => appConfig);
  const brandEntity = useAppSelector(({ brandEntity }) => brandEntity);
  const navigate = useNavigate();
  const matches = useMatches();

  const brandName = brandNameFromLocationState ?? brandEntity?.entity?.name;
  const brandId = brandIdFromLocationState ?? brandEntity?.entity?.brandId;

  const headerLinkTo = getHeaderLink(appConfig, brandName, brandId);
  const pageNavConfig = getPageNavConfig(matches as any);

  const backButtonConfig = pageNavConfig?.back;
  const closeButtonConfig = pageNavConfig?.close;

  const handleToBag = () => {
    navigate("/mybag");
  };

  const handleBack = () => {
    // check if we are able to go back
    if (window.history.state.idx > 0) {
      navigate(-1);
    } else {
      handleToShopDomain();
    }
  };

  const handleSignout = () => {
    signOut();
  };

  const handleToShopDomain = () => {
    if (headerLinkTo.external) {
      window.location.href = headerLinkTo.link;
    } else {
      navigate(headerLinkTo.link);
    }
  };

  const handleSkipUserSignUpFlow = () => {
    send({ type: "SKIP_USER_SIGN_UP_FLOW" });
  };

  const actionMapping = {
    [StoreNavAction.Back]: handleBack,
    [StoreNavAction.SignOut]: handleSignout,
    [StoreNavAction.ToBag]: handleToBag,
    [StoreNavAction.ToShopDomain]: handleToShopDomain,
    [StoreNavAction.SkipUserSignUpFlow]: handleSkipUserSignUpFlow,
  };

  const onBackClick = backButtonConfig
    ? actionMapping[backButtonConfig]
    : undefined;

  const onCloseClick = closeButtonConfig
    ? actionMapping[closeButtonConfig]
    : undefined;

  // Temporary - remove in the route config once we turn on new checkout
  const { enabled } = useAsyncCheckoutEnabled();
  const isCheckoutForm = location.pathname === "/checkout";
  const shouldHideCloseForNewCheckout = enabled && isCheckoutForm;

  return (
    <PageHeaderStyled
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      style={{ zIndex: 5 }}
    >
      {onBackClick ? (
        <div className="back_button">
          <BackButton onClick={onBackClick} iconSize={20} />
        </div>
      ) : null}

      {pageNavConfig?.hasHamburgerMenu && <HeaderMenuResponsiveMenu />}
      <BrandLogo
        loading={brandEntity?.loading}
        brandId={brandIdFromLocationState ?? brandEntity?.id}
        to={headerLinkTo.link}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleToShopDomain();
        }}
      />
      {pageNavConfig?.hasViewBagButton && <HeaderBagButton />}
      {onCloseClick && !shouldHideCloseForNewCheckout ? (
        <div className="close_button">
          <CloseButton iconSize={20} onClick={onCloseClick} />
        </div>
      ) : null}
    </PageHeaderStyled>
  );
};

const StoreSinglePageOutlet = () => {
  const ref = useRef<HTMLDivElement>(null);
  useSiteEntityManager();
  return (
    <div
      ref={ref}
      className="store_single_page_outlet"
      style={{ height: "100svh", overflowX: "clip", overflowY: "auto" }}
    >
      <StoreSinglePageWrapperContext.Provider value={{ wrapper: ref }}>
        <PageHeader />
        <PageBody />
      </StoreSinglePageWrapperContext.Provider>
    </div>
  );
};

export default StoreSinglePageOutlet;
